import { apiClient } from '@/api/client';

/** Recipient status as returned by the backend (mirrors Prisma ProfileShareStatus). */
export type RecipientStatus = 'active' | 'expired' | 'revoked' | 'pending';

/** A row of the Shared Profile Recipients table (mirrors backend ShareRecipientView). */
export interface ShareRecipient {
  id: string;
  recipientEmail: string;
  /** Always present now that the backend requires it; empty string for legacy pre-migration rows. */
  recipientName: string;
  status: RecipientStatus;
  expiresAt: string;
  otpVerified: boolean;
  firstAccessedAt: string | null;
  lastAccessedAt: string | null;
  accessCount: number;
  createdAt: string;
}

const base = (entityId: string) => `/startups/${entityId}/shares`;

/** List an entity's Shared Startup Profile recipients (MYN-54). */
export async function listRecipients(entityId: string): Promise<ShareRecipient[]> {
  const { data } = await apiClient.get<ShareRecipient[]>(base(entityId));
  return data;
}

/** Add a new recipient and email them a share link (MYN-41/54). */
export async function addRecipient(
  entityId: string,
  { email, recipientName }: { email: string; recipientName: string },
): Promise<ShareRecipient> {
  const { data } = await apiClient.post<ShareRecipient>(base(entityId), {
    recipientEmail: email,
    recipientName,
  });
  return data;
}

/** Revoke a recipient's access immediately (MYN-54 "remove" / MYN-57 "revoke") - idempotent. */
export async function revokeRecipient(entityId: string, recipientId: string): Promise<void> {
  await apiClient.post(`${base(entityId)}/${recipientId}/revoke`);
}

/** Regenerate a recipient's share link: new token, resets verification, re-sends the invite (MYN-57). */
export async function regenerateRecipient(entityId: string, recipientId: string): Promise<ShareRecipient> {
  const { data } = await apiClient.post<ShareRecipient>(`${base(entityId)}/${recipientId}/regenerate`);
  return data;
}
